import type { ITravelerInfo } from '@pcln/horizon'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react'

import { normalizeFormDateString, resolveFormDatesWithDefaults } from '@/shared-utils/date-helper'
import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'

import { initialState } from '../SearchForm/utils'
import { fetchNlSearch, getBrowserTimezone } from './aiSearch'
import { hasAiSearchMinLength } from './aiSearchConstants'
import {
  applyMappedResponse,
  areTravelersEqual,
  createInterpretation,
  getVisibleRemovableFilters,
  interpretationFromMapped,
  mapNlSearchResponse,
  toTravelerState,
  type AiSearchInterpretation,
  type UserFieldOverrides,
} from './aiSearchMap'
import { getExamplePreset } from './aiSearchPresets'
import type { AiSearchSubmitParams } from './aiSearchSubmit'

const SUGGESTION_FETCH_DEBOUNCE_MS = 300

function isAbortError(error: unknown) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

const EMPTY_OVERRIDES: UserFieldOverrides = {
  city: false,
  dates: false,
  travelers: false,
}

export type { AiSearchSubmitParams }

type UseAiSearchFormParams = {
  initialStartDate?: string
  initialEndDate?: string
  initialTravelers?: TravelerState
  onSubmitQuery: (params: AiSearchSubmitParams) => Promise<void> | void
}

export function useAiSearchForm({
  initialStartDate = '',
  initialEndDate = '',
  initialTravelers = initialState.travelers,
  onSubmitQuery,
}: UseAiSearchFormParams) {
  // The date chip and the relax URL both need a real range, so an empty initial range picks up
  // today/+1 here — once, at the point state is created — instead of at submit time.
  const initialDates = useMemo(
    () => resolveFormDatesWithDefaults(initialStartDate, initialEndDate),
    [initialEndDate, initialStartDate],
  )

  const [inputValue, setInputValue] = useState('')
  const [interpretation, setInterpretation] = useState<AiSearchInterpretation>(() =>
    createInterpretation({ ...initialDates, travelers: initialTravelers }),
  )
  const [overrides, setOverrides] = useState<UserFieldOverrides>(EMPTY_OVERRIDES)
  const [removedFilterIds, setRemovedFilterIds] = useState<Set<string>>(() => new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasParseError, setHasParseError] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [showExamples, setShowExamples] = useState(true)
  const [hasUnresolvedDestination, setHasUnresolvedDestination] = useState(false)

  const inputId = useId()
  const helperTextId = useId()
  const submitErrorId = useId()

  const abortControllerRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null)
  const interpretationRef = useRef(interpretation)
  const overridesRef = useRef(overrides)
  const fetchInterpretationRef = useRef<(searchQuery: string) => Promise<void>>(() => Promise.resolve())

  interpretationRef.current = interpretation
  overridesRef.current = overrides

  const fallbackInterpretation = useMemo(
    () => createInterpretation({ ...initialDates, travelers: initialTravelers }),
    [initialDates, initialTravelers],
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortControllerRef.current?.abort()
    }
  }, [])

  const applyResponse = useCallback(
    (mapped: ReturnType<typeof mapNlSearchResponse>, replaceAll: boolean) => {
      const next = replaceAll
        ? interpretationFromMapped(mapped, fallbackInterpretation)
        : applyMappedResponse(interpretationRef.current, mapped, overridesRef.current)

      setInterpretation(next)
      setHasParseError(false)
      // Penny answers the query itself, so it is the one route that needs no destination.
      setHasUnresolvedDestination(!next.city && !next.isPennyEligible)
    },
    [fallbackInterpretation],
  )

  const fetchInterpretation = useCallback(
    async (searchQuery: string) => {
      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller

      setIsFetching(true)
      setHasParseError(false)
      try {
        const response = await fetchNlSearch({
          query: searchQuery,
          timezone: getBrowserTimezone(),
          signal: controller.signal,
        })

        if (!mountedRef.current || abortControllerRef.current !== controller) return

        applyResponse(mapNlSearchResponse(response), false)
      } catch (error) {
        if (isAbortError(error) || !mountedRef.current || abortControllerRef.current !== controller) return
        setHasParseError(true)
        setInterpretation((current) => ({ ...current, isPennyEligible: true }))
      } finally {
        if (mountedRef.current && abortControllerRef.current === controller) {
          setIsFetching(false)
        }
      }
    },
    [applyResponse],
  )

  fetchInterpretationRef.current = fetchInterpretation

  // Debounce is created once. Recreating it when fetchInterpretation changes would cancel a
  // pending call (isFetching already true) and abort in-flight work without replaying the query.
  useEffect(() => {
    const debouncedFetch = debounce((searchQuery: string) => {
      void fetchInterpretationRef.current(searchQuery)
    }, SUGGESTION_FETCH_DEBOUNCE_MS)
    debouncedFetchRef.current = debouncedFetch

    return () => {
      debouncedFetch.cancel()
      abortControllerRef.current?.abort()
    }
  }, [])

  const resetToFallback = useCallback(() => {
    abortControllerRef.current?.abort()
    debouncedFetchRef.current?.cancel()
    setInterpretation(fallbackInterpretation)
    setOverrides(EMPTY_OVERRIDES)
    setRemovedFilterIds(new Set())
    setHasParseError(false)
    setHasUnresolvedDestination(false)
    setIsFetching(false)
    setShowExamples(true)
  }, [fallbackInterpretation])

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value)
      setHasParseError(false)
      setHasUnresolvedDestination(false)

      if (!hasAiSearchMinLength(value)) {
        resetToFallback()
        return
      }

      setShowExamples(false)
      setIsFetching(true)
      debouncedFetchRef.current?.(value.trim())
    },
    [resetToFallback],
  )

  const handleExampleSelect = useCallback(
    (label: string) => {
      const preset = getExamplePreset(label)
      if (!preset) return

      abortControllerRef.current?.abort()
      debouncedFetchRef.current?.cancel()
      setInputValue(label)
      setOverrides(EMPTY_OVERRIDES)
      setRemovedFilterIds(new Set())
      setShowExamples(false)
      setIsFetching(false)
      applyResponse(mapNlSearchResponse(preset), true)
      document.getElementById(inputId)?.focus()
    },
    [applyResponse, inputId],
  )

  const handleDatesChange = useCallback((nextStartDate: string, nextEndDate: string) => {
    const current = interpretationRef.current
    const seededEmptyRange = !current.startDate && !current.endDate
    setInterpretation({
      ...current,
      startDate: normalizeFormDateString(nextStartDate),
      endDate: normalizeFormDateString(nextEndDate),
    })
    if (!seededEmptyRange) {
      setOverrides((overridesState) => ({ ...overridesState, dates: true }))
    }
  }, [])

  const handleTravelersChange = useCallback((travelers: ITravelerInfo) => {
    const current = interpretationRef.current
    const nextTravelers = toTravelerState(travelers)
    setInterpretation({ ...current, travelers: nextTravelers })

    // Opening the chip echoes the current values back; only a real edit should stop
    // later responses from updating the travelers.
    if (!areTravelersEqual(current.travelers, nextTravelers)) {
      setOverrides((overridesState) => ({ ...overridesState, travelers: true }))
    }
  }, [])

  const handleCityChange = useCallback((city: LOCATION_SEARCH_TYPE | null) => {
    setInterpretation((current) => ({ ...current, city }))
    setOverrides((current) => ({ ...current, city: true }))
    setHasUnresolvedDestination(!city)
  }, [])

  const handleRemoveFilter = useCallback((id: string) => {
    setRemovedFilterIds((current) => new Set(current).add(id))
  }, [])

  const hasQuery = hasAiSearchMinLength(inputValue)
  const removableFilters = useMemo(
    () => getVisibleRemovableFilters(interpretation, removedFilterIds),
    [interpretation, removedFilterIds],
  )

  // Penny answers the query itself, so it is the one route that needs no destination.
  const needsDestination = !interpretation.city && !interpretation.isPennyEligible
  // Penny owns the whole query, so interpreted chips would only be noise on the way out.
  const showFilterChips = hasQuery && !interpretation.isPennyEligible
  const showStandardSearchFallback = hasParseError || hasUnresolvedDestination

  const isSubmitDisabled = !hasQuery || isFetching || isSubmitting || needsDestination

  const inputDescribedBy = [helperTextId, showStandardSearchFallback ? submitErrorId : null]
    .filter(Boolean)
    .join(' ')

  const handleSubmit = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim()
      if (!hasAiSearchMinLength(trimmedQuery) || isSubmitting || isFetching) return

      const current = interpretationRef.current
      if (!current.city && !current.isPennyEligible) return

      setIsSubmitting(true)
      try {
        await onSubmitQuery({
          query: trimmedQuery,
          startDate: current.startDate,
          endDate: current.endDate,
          travelers: current.travelers,
          city: current.city,
          filters: current.filters,
          priceRange: current.priceRange,
          removedFilterIds,
          isPennyEligible: current.isPennyEligible,
        })
      } catch {
        setHasParseError(true)
        setInterpretation((currentInterpretation) => ({ ...currentInterpretation, isPennyEligible: true }))
      } finally {
        setIsSubmitting(false)
      }
    },
    [isFetching, isSubmitting, onSubmitQuery, removedFilterIds],
  )

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void handleSubmit(inputValue)
    },
    [handleSubmit, inputValue],
  )

  const handleInputSubmit = useCallback(
    (query: string) => {
      void handleSubmit(query)
    },
    [handleSubmit],
  )

  return {
    inputValue,
    interpretation,
    isSubmitting,
    hasParseError,
    hasQuery,
    showFilterChips,
    showStandardSearchFallback,
    isFetching,
    showExamples,
    isSubmitDisabled,
    inputId,
    helperTextId,
    submitErrorId,
    inputDescribedBy,
    removableFilters,
    handleFormSubmit,
    handleInputSubmit,
    handleInputChange,
    handleDatesChange,
    handleTravelersChange,
    handleCityChange,
    handleRemoveFilter,
    handleExampleSelect,
  }
}
